"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import {
  ANNOUNCEMENT_CAMPAIGN_POLL_MS,
  announcementCampaignMsUntilDue,
  announcementCampaignTimerDelayMs,
  isAnnouncementCampaignActive,
} from "../announcement-campaign";
import type {
  AnnouncementCampaignProgress,
  CancelAnnouncementCampaignResult,
  StartAnnouncementCampaignInput,
  StartAnnouncementCampaignResult,
} from "../types";

export interface AnnouncementCampaignApi {
  start: (
    input: StartAnnouncementCampaignInput,
  ) => Promise<StartAnnouncementCampaignResult>;
  getProgress: (
    campaignId: string,
  ) => Promise<AnnouncementCampaignProgress | null>;
  tick: (
    campaignId: string,
  ) => Promise<AnnouncementCampaignProgress | null>;
  cancel?: (
    campaignId: string,
  ) => Promise<CancelAnnouncementCampaignResult>;
}

export interface UseAnnouncementCampaignOptions {
  pollMs?: number;
  initialCampaign?: AnnouncementCampaignProgress | null;
}

export interface UseAnnouncementCampaignResult {
  campaign: AnnouncementCampaignProgress | null;
  starting: boolean;
  error: string | null;
  start: (input: StartAnnouncementCampaignInput) => Promise<void>;
  cancel: () => Promise<void>;
  reset: () => void;
}

export function useAnnouncementCampaign(
  api: AnnouncementCampaignApi,
  options: UseAnnouncementCampaignOptions = {},
): UseAnnouncementCampaignResult {
  const pollMs = options.pollMs ?? ANNOUNCEMENT_CAMPAIGN_POLL_MS;
  const [campaign, setCampaign] = useState<AnnouncementCampaignProgress | null>(
    options.initialCampaign ?? null,
  );
  const [starting, setStarting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const campaignIdRef = useRef(campaign?.campaignId ?? null);

  useEffect(() => {
    campaignIdRef.current = campaign?.campaignId ?? null;
  }, [campaign?.campaignId]);

  const reset = useCallback(() => {
    setCampaign(null);
    setStarting(false);
    setError(null);
    campaignIdRef.current = null;
  }, []);

  const start = useCallback(
    async (input: StartAnnouncementCampaignInput) => {
      setStarting(true);
      setError(null);
      try {
        const result = await api.start(input);
        if (result.ok) {
          campaignIdRef.current = result.campaign.campaignId;
          setCampaign(result.campaign);
          return;
        }
        setError(result.error);
        if (result.campaign) {
          campaignIdRef.current = result.campaign.campaignId;
          setCampaign(result.campaign);
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : "Failed to send emails");
      } finally {
        setStarting(false);
      }
    },
    [api],
  );

  const cancel = useCallback(async () => {
    const campaignId = campaignIdRef.current;
    if (!campaignId || !api.cancel) {
      return;
    }
    setError(null);
    try {
      const result = await api.cancel(campaignId);
      if (result.ok) {
        setCampaign(result.campaign);
        return;
      }
      setError(result.error);
      if (result.campaign) {
        setCampaign(result.campaign);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to send emails");
    }
  }, [api]);

  const campaignId = campaign?.campaignId ?? null;
  const isActive = campaign
    ? isAnnouncementCampaignActive(campaign.status)
    : false;
  const scheduledAt = campaign?.scheduledAt ?? null;

  useEffect(() => {
    if (!campaignId || !isActive) {
      return;
    }

    let cancelled = false;
    let intervalId: number | undefined;
    let timeoutId: number | undefined;

    const poll = async () => {
      try {
        const next = await api.tick(campaignId);
        if (cancelled || campaignIdRef.current !== campaignId) {
          return;
        }
        if (next) {
          setCampaign(next);
        }
      } catch (err) {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : "Failed to send emails");
      }
    };

    const startTicking = () => {
      intervalId = window.setInterval(() => {
        void poll();
      }, pollMs);
      void poll();
    };

    const armWait = () => {
      const delayMs = announcementCampaignTimerDelayMs(scheduledAt);
      if (delayMs <= 0) {
        startTicking();
        return;
      }
      timeoutId = window.setTimeout(() => {
        if (announcementCampaignMsUntilDue(scheduledAt) <= 0) {
          startTicking();
          return;
        }
        armWait();
      }, delayMs);
    };

    armWait();

    return () => {
      cancelled = true;
      if (timeoutId != null) {
        window.clearTimeout(timeoutId);
      }
      if (intervalId != null) {
        window.clearInterval(intervalId);
      }
    };
  }, [api, campaignId, isActive, pollMs, scheduledAt]);

  return {
    campaign,
    starting,
    error,
    start,
    cancel,
    reset,
  };
}
